import EventEmitter from "events";
import { nanoid } from "nanoid";
import { store } from "src/store/store";
import debug from "src/modules/debug-logger";
import { getWeekDays, getCurrentDayName } from "src/utils/weekDays";
import { getLocale } from "src/utils/locale";
import { getXmppLogin, getDeviceName } from "src/modules/auth/duck/selectors";
import { getDefaultAvatar } from "src/modules/avatar/duck/selectors";
import {
  createStartScheduledMonitoringNotification,
  createStopScheduledMonitoringNotification
} from "./createNotification";
import { getTasks, getEnabled, getDevicesToNotify } from "./duck/selectors";
import { addTask, removeTask, updateTask } from "./duck/actions";

const ONE_MINUTE = 60000;
const States = { inactive: "inactive", active: "active" };

export default class Scheduler extends EventEmitter {
  scheduledTasks = [];
  activeTasks = [];
  state = States.inactive;
  tickId = null;
  ignoreTaskStart = false;

  weekDays = getWeekDays();
  localizedWeekDays = getWeekDays(getLocale());

  constructor(messenger) {
    super();
    this.messenger = messenger;
    this.tick = this.tick.bind(this);
    this.getNextTask = this.getNextTask.bind(this);
    this.handleEnableChange = this.handleEnableChange.bind(this);

    this.unsubscribe = store.subscribe(this.handleEnableChange);
    if (this.isEnabled) this.start();
  }

  get tasks() {
    return getTasks(store.getState());
  }
  get isEnabled() {
    return getEnabled(store.getState());
  }
  get devicesToNotify() {
    return getDevicesToNotify(store.getState());
  }
  get jid() {
    return getXmppLogin(store.getState());
  }
  get deviceName() {
    return getDeviceName(store.getState());
  }
  get selectedAvatar() {
    return getDefaultAvatar(store.getState());
  }
  get dispatch() {
    return store.dispatch;
  }
  get hasActiveTask() {
    return this.activeTasks.length !== 0;
  }
  get dayName() {
    return getCurrentDayName();
  }
  get dayInMs() {
    return 1 * 24 * 60 * 60 * 1000;
  }
  get currentMs() {
    return this.getMilliseconds({
      hour: new Date().getHours(),
      minute: new Date().getMinutes()
    });
  }

  handleEnableChange() {
    const isEnabled = getEnabled(store.getState());
    if (this.state === States.active && !isEnabled) {
      this.stop();
    }
    if (this.state === States.inactive && isEnabled) {
      this.start();
    }
  }

  start() {
    this.scheduledTasks = this.tasks;
    if (this.scheduledTasks.length === 0) return;
    this.tickId = setInterval(this.tick, ONE_MINUTE);
    this.state = States.active;
    this.onStateChange();
  }

  getActiveTasksByType(type) {
    return this.activeTasks.filter((task) => task.type === type);
  }

  getTaskById(id) {
    return this.scheduledTasks.filter((t) => t.id === id)?.[0];
  }

  getIsTaskActive(task) {
    return Boolean(this.activeTasks.filter((t) => t.id === task.id)?.[0]);
  }

  getIsOverlaping(taskToCheck) {
    let overlaps = false;
    let isOverNight = false;
    const { startTime, endTime, days, id } = taskToCheck;
    const startInMs = this.getMilliseconds(startTime);
    const endInMs = this.getMilliseconds(endTime);
    if (startInMs > endInMs) isOverNight = true;
    this.scheduledTasks
      .filter((t) => t.id !== id)
      .forEach((task) => {
        const hasSameDays = task.days.some((d) => days.includes(d));
        if (!hasSameDays && !isOverNight) return;
        const taskStartInMs = this.getMilliseconds(task.startTime);
        const taskEndInMs = this.getMilliseconds(task.endTime);
        if (hasSameDays) {
          if (startInMs >= taskStartInMs && startInMs <= taskEndInMs) {
            overlaps = true;
          }
          if (endInMs <= taskEndInMs && endInMs >= taskStartInMs) {
            overlaps = true;
          }
          if (startInMs <= taskStartInMs && endInMs >= taskEndInMs) {
            overlaps = true;
          }
        }
        if (!hasSameDays && isOverNight) {
          days.forEach((day) => {
            const dayIndex = this.getDayIndex(day);
            const selector = dayIndex + 1 > 6 ? 0 : dayIndex + 1;
            const nextDay = this.weekDays[selector];

            const existsInAnotherTask = this.scheduledTasks
              .filter((t) => t.id !== id)
              .filter((t) => {
                const startMs = this.getMilliseconds(t.startTime);

                const timeOverlaps = endInMs >= startMs;
                const includesDay = t.days.includes(nextDay);

                return timeOverlaps && includesDay;
              });
            if (existsInAnotherTask.length !== 0) {
              overlaps = true;
            }
          });
        }
      });
    return overlaps;
  }

  getNextTask() {
    const tasksAndDays = {};
    const currentDay = this.dayName;
    const currentDayIndex = this.getDayIndex(currentDay);
    this.scheduledTasks.forEach((task, i) => {
      const { days, startTime } = task;
      const startTimeInMs = this.getMilliseconds(startTime);
      let closestDayDif;
      days.forEach((day) => {
        const dayIndex = this.getDayIndex(day);
        const dayDifference =
          currentDayIndex > dayIndex
            ? Math.abs(currentDayIndex + dayIndex - 7)
            : dayIndex - currentDayIndex;
        if (dayDifference === 0 && this.currentMs <= startTimeInMs) {
          closestDayDif = 0;
        }
        if (dayDifference === 0 && this.currentMs > startTimeInMs) {
          const nextDayIndex = this.getDayIndex(days[i + 1] || days[0]);
          let nextDayDifference =
            currentDayIndex > nextDayIndex
              ? currentDayIndex + nextDayIndex + 1
              : nextDayIndex - currentDayIndex;
          if (nextDayDifference === 0) nextDayDifference = 7;
          closestDayDif = Number(`${nextDayDifference}.${startTimeInMs}`);
        }

        if (!closestDayDif && dayDifference !== 0) {
          closestDayDif = Number(`${dayDifference}.${startTimeInMs}`);
        }
        if (closestDayDif > dayDifference && dayDifference !== 0) {
          closestDayDif = Number(`${dayDifference}.${startTimeInMs}`);
        }
      });
      tasksAndDays[task.id] = closestDayDif;
    });
    let idOfTask;
    let closestDayDif;
    Object.entries(tasksAndDays).forEach(([id, dayDiff]) => {
      if (closestDayDif == null) {
        closestDayDif = dayDiff;
        idOfTask = id;
      }
      if (closestDayDif > dayDiff) {
        closestDayDif = dayDiff;
        idOfTask = id;
      }
    });
    return this.getTaskById(idOfTask);
  }

  tick() {
    debug.scheduler("tick");

    this.scheduledTasks.forEach((task) => {
      const isActive = this.getIsTaskActive(task);
      if (isActive) return;
      const { startTime } = task;

      const startInMs = this.getMilliseconds(startTime);

      let yesterdayIndex = this.getDayIndex(this.dayName) - 1;
      if (yesterdayIndex < 0) yesterdayIndex = 6;

      if (this.currentMs === startInMs) {
        debug.scheduler("About to start task: ", task);

        this.activeTasks.push(task);
        this.onTaskStart(task);
      }
    });

    this.activeTasks.forEach((task) => {
      const { endTime } = task;
      const endInMs = this.getMilliseconds(endTime);
      const hasEnded = this.currentMs === endInMs;

      if (!hasEnded) return;
      debug.scheduler("About to end task: ", task);
      this.onTaskEnd(task);
      this.activeTasks = this.activeTasks.filter((t) => t.id !== task.id);
    });
  }

  checkTasks = () => {
    debug.scheduler("checking tasks ", this.scheduledTasks);
    this.scheduledTasks.forEach((task) => {
      const isActive = this.getIsTaskActive(task);
      if (isActive) return;

      const currentDay = this.dayName;

      const { days, startTime, endTime } = task;

      const startInMs = this.getMilliseconds(startTime);
      const endInMs = this.getMilliseconds(endTime);

      const isOverNight = startInMs > endInMs;
      let yesterdayIndex = this.getDayIndex(this.dayName) - 1;
      if (yesterdayIndex < 0) yesterdayIndex = 6;

      const startedYesterday =
        isOverNight && days.includes(this.weekDays[yesterdayIndex]);
      const isToday = days.includes(currentDay);

      const currentMs = this.currentMs;
      const isInTimeWindow = currentMs >= startInMs && currentMs < endInMs;

      if (
        (isToday && isInTimeWindow) ||
        (startedYesterday && currentMs < endInMs)
      ) {
        debug.scheduler("Setting task as active ", task);

        this.activeTasks.push(task);
      }
    });
  };

  getMilliseconds(time) {
    const { hour, minute } = time;
    return Number(hour) * (60000 * 60) + Number(minute) * 60000;
  }

  getClosestDay(task, localized = false) {
    const today = this.dayName;
    const todayIndex = this.getDayIndex(today);
    const startInMs = this.getMilliseconds(task.startTime);

    if (task.days.length === 1) {
      const day = task.days[0];
      return localized ? this.localizedWeekDays[this.getDayIndex(day)] : day;
    }

    if (task.days.includes(today) && this.currentMs <= startInMs) {
      return localized ? this.localizedWeekDays[todayIndex] : today;
    }

    if (todayIndex === 6) {
      if (localized) {
        return this.localizedWeekDays[this.getDayIndex(task.days[0])];
      }
      return task.days[0];
    }
    const closestDay = task.days.reduce((closestDay, day, i) => {
      if (closestDay) return closestDay;
      const dayIndex = this.getDayIndex(day);
      if (todayIndex > dayIndex) return closestDay;
      const isToday = task.days.includes(today);
      let nextDay = isToday ? task.days[i + 1] : task.days[i];
      if (!nextDay) nextDay = task.days[0];
      const nextDayIndex = this.getDayIndex(nextDay);

      const result = localized
        ? this.localizedWeekDays[nextDayIndex]
        : this.weekDays[nextDayIndex];

      closestDay = result;
      return closestDay;
    }, null);
    if (!closestDay) return task.days[0];
    return closestDay;
  }

  onTaskStart(task) {
    if (this.ignoreTaskStart) return;
    debug.scheduler("task started: ", task);
    this.emit("task-start", task);
  }

  onTaskEnd(task) {
    debug.scheduler("task ended: ", task);
    this.emit("task-end", task);
  }

  onTaskUpdate(task) {
    debug.scheduler("task updated: ", task);
    this.emit("task-update", task);
  }

  canRegisterTask(task) {
    const overlaps = this.getIsOverlaping(task);
    return !overlaps;
  }

  registerTask(
    task = {
      id: nanoid(),
      type: undefined,
      days: [],
      startTime: { hour: 0, minute: 0 },
      endTime: { hour: 0, minute: 0 }
    }
  ) {
    this.dispatch(addTask(task));
    this.scheduledTasks.push(task);
  }

  unregisterTask(task) {
    this.dispatch(removeTask(task));
    this.scheduledTasks = this.scheduledTasks.filter(
      ({ id }) => id !== task.id
    );
  }

  updateTask(task) {
    this.dispatch(updateTask(task));
    this.scheduledTasks = [...this.tasks];
    const isActive = this.getIsTaskActive(task);

    if (isActive) {
      const endDate = new Date(
        new Date().setHours(task.endTime.hour, task.endTime.minute)
      ).getTime();
      const now = Date.now();
      const hasEnded = now > endDate;
      if (hasEnded) {
        debug.scheduler("Settings task as inactive: ", task);
        this.activeTasks = this.activeTasks.filter((t) => t.id !== task.id);
      } else {
        debug.scheduler("Updating active task: ", task);
        this.activeTasks = this.activeTasks.map((t) => {
          if (t.id === task.id) return { ...t, ...task };
          return t;
        });
      }
    }

    this.onTaskUpdate(task);
  }

  cancelActiveTasks() {
    this.activeTasks = [];
  }

  getDaysAdjective(
    { days, t, addSeparation, short } = {
      days: [],
      t: undefined,
      addSeparation: false,
      short: false
    }
  ) {
    if (days.length === 0) return [];
    if (days.length === 7) {
      return t ? t("everyday") : "everyday";
    }
    if (
      days.length === 2 &&
      days.includes("Saturday") &&
      days.includes("Sunday")
    ) {
      return t ? t("weekend") : "weekend";
    }
    if (
      days.length === 5 &&
      !days.includes("Saturday") &&
      !days.includes("Sunday")
    ) {
      return t ? t("weekdays") : "weekdays";
    }
    const daysAdjective = days.map((d) => {
      const engDayIndex = this.weekDays.indexOf(d);
      if (short) {
        return this.localizedWeekDays[engDayIndex].slice(0, 2);
      }
      return this.localizedWeekDays[engDayIndex];
    });
    return addSeparation ? daysAdjective.join(", ") : daysAdjective;
  }

  getDayIndex(day) {
    return this.weekDays.indexOf(day);
  }

  sendStartNotification(monitoringId) {
    const notification = createStartScheduledMonitoringNotification({
      jid: this.jid,
      deviceName: this.deviceName,
      monitoringId,
      avatarName: this.selectedAvatar.name,
      avatarSex: this.selectedAvatar.sex
    });

    Object.values(this.devicesToNotify).forEach((device) => {
      this.messenger.sendPushNotification(device.jid, notification);
    });
  }

  sendStopNotification(monitoringId) {
    const notification = createStopScheduledMonitoringNotification({
      jid: this.jid,
      deviceName: this.deviceName,
      monitoringId,
      avatarName: this.selectedAvatar.name,
      avatarSex: this.selectedAvatar.sex
    });
    Object.values(this.devicesToNotify).forEach((device) => {
      this.messenger.sendPushNotification(device.jid, notification);
    });
  }

  onStateChange() {
    debug.scheduler("state changed: ", this.state);
    this.emit("state-change", this.state);
  }

  stop() {
    clearInterval(this.tickId);
    this.state = States.inactive;
    this.activeTasks = [];
    this.scheduledTasks = [];
    this.onStateChange();
    this.unsubscribe();
  }
}
